import React from "react";
import { useTranslation } from "react-i18next";
import { CloudSun, FileText, Fish, MapPin, TriangleAlert } from "lucide-react";
import { trackProductEvent } from "../../analytics/trackEvent";
import "../../styles/components/WeatherOverlay.css";

// Import the sub-components
import AnglersRadiusPanel from "./WeatherOverlay/AnglersRadiusPanel";
import FishInfoModal from "./WeatherOverlay/FishInfoModal";
import GroupsRadiusPanel from "./WeatherOverlay/GroupsRadiusPanel";
import WeatherTab from "./WeatherOverlay/tabs/WeatherTab";
import FishTab from "./WeatherOverlay/tabs/FishTab";
import RulesTab from "./WeatherOverlay/tabs/RulesTab";
import DangerTab from "./WeatherOverlay/tabs/DangerTab";
import "../../styles/utils/weatherPresentation.css";

function isIphoneBrowser() {
  if (typeof navigator === "undefined") {
    return false;
  }

  return /iPhone/i.test(navigator.userAgent || "");
}

const MAP_TAB_ANALYTICS_EVENTS = {
  weather: "map_weather_tab_opened",
  fish: "map_fish_species_tab_opened",
  info: "map_fishing_info_tab_opened",
  danger: "map_dangers_tab_opened",
};

/* ---------------------- Info Panel ---------------------- */
function InfoPanel({
  data,
  loading = false,
  error = false,
  onClose,
  onPickFish,
  tab = "weather",
  onTabChange,
  panelView = "tabs",
  onPanelViewChange,
  onOpenProfile,
  onOpenGroup,
  viewer,
  onAddFavoriteSpot,
  isSimpleModeActive = false,
}) {
  const { t } = useTranslation();
  const isiPhone = isIphoneBrowser();
  const lastTouchTabRef = React.useRef({
    name: "",
    at: 0,
  });

  const activateTab = React.useCallback(
    (nextTab) => {
      onTabChange?.(nextTab);
      const eventName = MAP_TAB_ANALYTICS_EVENTS[nextTab];
      if (eventName) {
        trackProductEvent(eventName, {
          featureArea: "map",
          metadata: { tab_name: nextTab },
        });
      }
    },
    [onTabChange]
  );

  const handleTabTouchEnd = React.useCallback(
    (nextTab, event) => {
      if (!isiPhone) {
        return;
      }

      if (event?.cancelable) {
        event.preventDefault();
      }
      event?.stopPropagation?.();

      lastTouchTabRef.current = {
        name: nextTab,
        at: Date.now(),
      };
      activateTab(nextTab);
    },
    [activateTab, isiPhone]
  );

  const handleTabClick = React.useCallback(
    (nextTab) => {
      if (isiPhone) {
        const lastTouch = lastTouchTabRef.current;
        if (
          lastTouch.name === nextTab &&
          Date.now() - lastTouch.at < 750
        ) {
          lastTouchTabRef.current = { name: "", at: 0 };
          return;
        }
      }

      activateTab(nextTab);
    },
    [activateTab, isiPhone]
  );

  if (loading) {
  return (
    <div className="info-panel">
      <div className="info-panel-loading">
        {t("common_loading", { defaultValue: "Loading..." })}
      </div>
    </div>
  );
}

if (!data) return null;

if (error && !data.isBasin) {
  return (
    <div className="info-panel">
      <div className="info-panel-error">{t("weather_unavailable")}</div>
    </div>
  );
}



  if (!data) return null;

  return (
    <div className="info-panel">
      <div className="info-panel-header">
        <div className="info-panel-location">
          <span className="info-panel-location-icon" aria-hidden="true">
            <MapPin />
          </span>
          <div className="info-panel-location-copy">
            <h2 className="info-panel-title">{data.placeName}</h2>
            <div className="info-panel-coords">
              {t("common_lat")}: {data.lat.toFixed(3)}, {t("common_lng")}:{" "}
              {data.lng.toFixed(3)}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="info-panel-close map-overlay-close-button"
          onClick={() => onClose("user")}
          aria-label={t("common_close")}
        >
          &times;
        </button>
      </div>

      <hr />

      {panelView === "anglers" ? (
        <AnglersRadiusPanel
          data={data}
          onOpenProfile={onOpenProfile}
        />
      ) : panelView === "groups" ? (
        <GroupsRadiusPanel
          data={data}
          onOpenGroup={onOpenGroup}
        />
      ) : (
        <>
      {!isSimpleModeActive && (
        <div className={`info-panel-tabs${isiPhone ? " info-panel-tabs--ios" : ""}`}>
          <button
            type="button"
            onClick={() => handleTabClick("weather")}
            onTouchEnd={(event) => handleTabTouchEnd("weather", event)}
            className={`info-panel-tab ${tab === "weather" ? "active" : ""}`}
          >
            <CloudSun aria-hidden="true" /> {t("weather_tabs_weather")}
          </button>

          <button
            type="button"
            onClick={() => handleTabClick("fish")}
            onTouchEnd={(event) => handleTabTouchEnd("fish", event)}
            className={`info-panel-tab ${tab === "fish" ? "active" : ""}`}
          >
            <Fish aria-hidden="true" /> {t("weather_tabs_fish")}
          </button>

          <button
            type="button"
            onClick={() => handleTabClick("info")}
            onTouchEnd={(event) => handleTabTouchEnd("info", event)}
            className={`info-panel-tab ${tab === "info" ? "active" : ""}`}
          >
            <FileText aria-hidden="true" /> {t("weather_tabs_info")}
          </button>

          <button
            type="button"
            onClick={() => handleTabClick("danger")}
            onTouchEnd={(event) => handleTabTouchEnd("danger", event)}
            className={`info-panel-tab danger ${tab === "danger" ? "active" : ""}`}
          >
            <TriangleAlert aria-hidden="true" /> {t("weather_tabs_danger")}
          </button>
        </div>
      )}

      {/* Render the specific tab component based on state */}
      {(isSimpleModeActive || tab === "weather") && (
        <WeatherTab
          data={data}
          viewer={viewer}
          onAddFavoriteSpot={onAddFavoriteSpot}
          isSimpleModeActive={isSimpleModeActive}
          onOpenAnglers={() => {
            onTabChange?.("weather");
            onPanelViewChange?.("anglers");
          }}
          onOpenGroups={() => {
            onTabChange?.("weather");
            onPanelViewChange?.("groups");
          }}
        />
      )}

      {!isSimpleModeActive && tab === "fish" && (
        <FishTab data={data} onPickFish={onPickFish} />
      )}

      {!isSimpleModeActive && tab === "info" && <RulesTab data={data} />}

      {!isSimpleModeActive && tab === "danger" && (
        <DangerTab data={data} onClose={() => onTabChange?.("weather")} />
      )}
        </>
      )}
    </div>
  );
}

/* ---------------------- Main Overlay Wrapper ---------------------- */
export default function WeatherOverlay({
  data,
  onClose,
  isOpen = true,
  initialTab = "weather",
  onTabChange,
  panelView = "tabs",
  onPanelViewChange,
  fish = null,
  onPickFish,
  onCloseFish,
  onFishipediaNavigate,
  loading = false,
  error = false,
  onOpenProfile,
  onOpenGroup,
  viewer,
  onAddFavoriteSpot,
  isSimpleModeActive = false,
}) {
  const previousSimpleModeRef = React.useRef(isSimpleModeActive);

  React.useEffect(() => {
    if (!isOpen || !isSimpleModeActive) {
      previousSimpleModeRef.current = isSimpleModeActive;
      return;
    }

    const becameSimpleMode = !previousSimpleModeRef.current;
    previousSimpleModeRef.current = isSimpleModeActive;

    if (initialTab !== "weather") {
      onTabChange?.("weather");
    }

    if (becameSimpleMode && panelView !== "tabs") {
      onPanelViewChange?.("tabs");
    }

    if (fish) {
      onCloseFish?.();
    }
  }, [
    fish,
    initialTab,
    isOpen,
    isSimpleModeActive,
    onCloseFish,
    onPanelViewChange,
    onTabChange,
    panelView,
  ]);

  if (!isOpen) return null;

  return (
    <>
      <InfoPanel
        data={data}
        loading={loading}
        error={error}
        onClose={onClose}
        onPickFish={onPickFish}
        tab={initialTab}
        onTabChange={onTabChange}
        panelView={panelView}
        onPanelViewChange={onPanelViewChange}
        onOpenProfile={onOpenProfile}
        onOpenGroup={onOpenGroup}
        viewer={viewer}
        onAddFavoriteSpot={onAddFavoriteSpot}
        isSimpleModeActive={isSimpleModeActive}
      />

      {fish && (
        <FishInfoModal
          fishName={fish.name}
          onClose={onCloseFish}
          onNavigateAway={onFishipediaNavigate}
          mapState={{
            lat: data?.lat,
            lng: data?.lng,
            environment: data?.environment || null,
          }}
        />
      )}
    </>
  );
}
